import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { blogPosts as mockPosts } from '../../data/mock';
import { blogService } from '../../services/blogService';
import { Calendar, Clock, ArrowRight, BookOpen } from 'lucide-react';
import { Button } from '../ui/button';

const Blog = () => {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadPosts();
  }, []);

  const loadPosts = async () => {
    try {
      const data = await blogService.getPosts({ perPage: 6 });
      if (data.posts && data.posts.length > 0) {
        setPosts(data.posts);
      } else {
        // Fall back to mock data if no posts in DB
        setPosts(mockPosts);
      }
    } catch (err) {
      // Fall back to mock data on error
      console.error('Using mock data:', err);
      setPosts(mockPosts);
    } finally {
      setLoading(false);
    }
  };

  const featuredPosts = posts.filter(post => post.featured).slice(0, 2);
  const recentPosts = posts.filter(post => !post.featured).slice(0, 3);

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('es-PA', { 
      day: 'numeric', 
      month: 'long', 
      year: 'numeric' 
    });
  };

  // Handle different field names between mock and API data
  const getDate = (post) => post.published_at || post.date || post.created_at;
  const getReadTime = (post) => post.read_time || post.readTime;

  return (
    <section id="blog" className="py-24 bg-gradient-to-b from-white to-slate-50 relative overflow-hidden">
      {/* Background decorations */}
      <div className="absolute top-1/4 right-0 w-72 h-72 bg-[#0092ca]/5 rounded-full blur-3xl" />
      <div className="absolute bottom-1/4 left-0 w-96 h-96 bg-[#0092ca]/5 rounded-full blur-3xl" />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
        {/* Section Header */}
        <div className="text-center max-w-3xl mx-auto mb-16">
          <span className="inline-flex items-center gap-2 px-4 py-2 bg-[#0092ca]/10 text-[#0092ca] text-sm font-semibold rounded-full mb-4">
            <BookOpen className="w-4 h-4" />
            Blog
          </span>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-gray-900 mb-6">
            Recursos para{' '}
            <span className="text-[#0092ca]">tu Negocio</span>
          </h2>
          <p className="text-lg text-gray-600">
            Respondemos tus preguntas sobre factoring, finanzas empresariales y 
            estrategias para hacer crecer tu empresa.
          </p>
        </div>

        {/* Featured Posts */}
        <div className="grid md:grid-cols-2 gap-8 mb-12">
          {featuredPosts.map((post) => (
            <article 
              key={post.id}
              className="group bg-white rounded-2xl overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-500 border border-gray-100 hover:border-[#0092ca]/30"
            >
              {/* Image */}
              <Link to={`/blog/${post.slug}`} className="block relative h-56 overflow-hidden">
                <img 
                  src={post.image} 
                  alt={post.title}
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                />
                <div className="absolute top-4 left-4">
                  <span className="px-3 py-1 bg-[#0092ca] text-white text-xs font-semibold rounded-full">
                    {post.category}
                  </span>
                </div>
              </Link>

              {/* Content */}
              <div className="p-6">
                <div className="flex items-center gap-4 text-sm text-gray-500 mb-3">
                  <span className="flex items-center gap-1">
                    <Calendar className="w-4 h-4" />
                    {formatDate(getDate(post))}
                  </span>
                  <span className="flex items-center gap-1">
                    <Clock className="w-4 h-4" />
                    {getReadTime(post)}
                  </span>
                </div>

                <Link to={`/blog/${post.slug}`}>
                  <h3 className="text-xl font-bold text-gray-900 mb-3 group-hover:text-[#0092ca] transition-colors line-clamp-2">
                    {post.title}
                  </h3>
                </Link>

                <p className="text-gray-600 mb-4 line-clamp-2">
                  {post.excerpt}
                </p>

                <Link 
                  to={`/blog/${post.slug}`}
                  className="inline-flex items-center gap-2 text-[#0092ca] font-semibold hover:gap-3 transition-all"
                >
                  Leer artículo
                  <ArrowRight className="w-4 h-4" />
                </Link>
              </div>
            </article>
          ))}
        </div>

        {/* Recent Posts Grid */}
        <div className="grid md:grid-cols-3 gap-6 mb-12">
          {recentPosts.map((post) => (
            <Link
              to={`/blog/${post.slug}`} 
              key={post.id}
              className="group bg-white rounded-xl p-6 shadow-md hover:shadow-xl transition-all duration-300 border border-gray-100 hover:border-[#0092ca]/30"
            >
              <span className="inline-block px-3 py-1 bg-gray-100 text-gray-600 text-xs font-medium rounded-full mb-4">
                {post.category}
              </span>

              <h4 className="text-lg font-bold text-gray-900 mb-2 group-hover:text-[#0092ca] transition-colors line-clamp-2">
                {post.title}
              </h4>

              <p className="text-gray-500 text-sm mb-4 line-clamp-2">
                {post.excerpt}
              </p>

              <div className="flex items-center justify-between text-sm text-gray-400">
                <span>{formatDate(getDate(post))}</span>
                <span>{getReadTime(post)}</span>
              </div>
            </Link>
          ))}
        </div>

        {/* CTA to see all posts */}
        <div className="text-center">
          <Link to="/blog">
            <Button 
              variant="outline"
              className="border-2 border-[#0092ca] text-[#0092ca] hover:bg-[#0092ca] hover:text-white px-8 py-6 rounded-full text-lg font-semibold transition-all duration-300"
            >
              Ver todos los artículos
              <ArrowRight className="w-5 h-5 ml-2" />
            </Button>
          </Link>
        </div>
      </div>
    </section>
  );
};

export default Blog;
